import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Subscription data returned from the check-subscription edge function
 */
export interface SubscriptionData {
  subscribed: boolean;
  subscription_tier: string | null;
  current_period_end: string | null;
  subscriptions: Array<{
    id: string;
    status: string;
    product_name: string;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  }>;
}

/**
 * Query key factory for subscription queries
 * Allows for easy invalidation and cache management
 */
export const subscriptionKeys = {
  all: ['subscription'] as const,
  detail: (userId: string | undefined) => [...subscriptionKeys.all, userId] as const,
};

/**
 * Fetches subscription status from Supabase edge function
 *
 * @param userId - The authenticated user's ID
 * @returns Subscription data or null if user is not authenticated
 * @throws Error if the subscription check fails
 */
const fetchSubscriptionStatus = async (userId: string | undefined): Promise<SubscriptionData | null> => {
  // Don't make the call if user is not authenticated
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase.functions.invoke('check-subscription');

  if (error) {
    throw new Error(`Failed to check subscription: ${error.message}`);
  }

  return {
    subscribed: data?.subscribed || false,
    subscription_tier: data?.subscription_tier || null,
    current_period_end: data?.current_period_end || null,
    subscriptions: data?.subscriptions || [],
  };
};

/**
 * React Query hook for fetching and caching subscription status
 *
 * Caches subscription data to eliminate redundant API calls:
 * - staleTime: 5 minutes - data is considered fresh for 5 minutes
 * - cacheTime: 10 minutes - cached data persists for 10 minutes after last use
 *
 * The hook will only execute the query when a user ID is provided (user is authenticated).
 * Cache is shared across all components using this hook, ensuring a single API call per session.
 *
 * @param userId - The authenticated user's ID (undefined if not authenticated)
 * @returns React Query result containing subscription data, loading state, and error state
 *
 * @example
 * ```tsx
 * function SubscriptionStatus() {
 *   const { data: { user } } = await supabase.auth.getUser();
 *   const { data, isLoading, error } = useSubscriptionQuery(user?.id);
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *   if (!data) return null;
 *
 *   return <div>Subscribed: {data.subscribed ? 'Yes' : 'No'}</div>;
 * }
 * ```
 */
export const useSubscriptionQuery = (userId: string | undefined) => {
  return useQuery({
    queryKey: subscriptionKeys.detail(userId),
    queryFn: () => fetchSubscriptionStatus(userId),
    // Cache configuration as per spec:
    // - staleTime: 5 minutes (data is fresh for 5 minutes before refetch)
    staleTime: 5 * 60 * 1000, // 5 minutes in milliseconds
    // - cacheTime: 10 minutes (cache persists for 10 minutes after last component unmounts)
    gcTime: 10 * 60 * 1000, // 10 minutes in milliseconds (gcTime is the new name for cacheTime in v5)
    // Only run query if we have a user ID (user is authenticated)
    enabled: !!userId,
    // Don't refetch on window focus to avoid unnecessary calls
    refetchOnWindowFocus: false,
    // Retry failed requests once
    retry: 1,
  });
};
